/**
 * SOLID Review
 * S — Strong. One concern: the table's video chat policy — turning it on and off, the idle rule, the
 *     server's removal, the banner, and reporting each kind of media use once per room.
 * O — Good. A new reason to turn video off is one more decision over `turnedOff`.
 * L — N/A — functions.
 * I — Good. Seven decisions on the one ClientDecisions contract, and the per-room view the screen reads.
 * D — Strong. The session draft and the port schemas.
 */
import { decided, type IEntityDecision, type TDecisionResult } from '../shared';
import { draftOf, settleSession } from '../session/sessionDraft';
import type { IPropsClientChanges, IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IStateClientSession } from '../../ports/schemas/ClientSession';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type { ICommandNothing } from '../../ports/schemas/TableMoves';
import {
  NO_MEDIA, type IEntityClientMedia, type ICommandMediaClock, type ICommandMediaIdle, type ICommandMediaBanner, type ICommandMediaUsed,
  type IEntityPresencePlan, type IPayloadMediaPresence,
} from '../../ports/schemas/MediaMoves';

type TSession<D> = TDecisionResult<IEntityDecision<IPropsClientChanges, D>>;

const NOTHING: IPropsClientChanges = {};
const APPLIED: IEntityTableAck = { applied: true };
const NOT_APPLIED: IEntityTableAck = { applied: false };
const NO_PRESENCE: IEntityPresencePlan = { request: null };

/**
 * The media state for `roomId`: the kept one if it is that room's, else a fresh one — the hook reset
 * everything when the room went, so a state kept for another room (or none) means nothing here.
 */
export function mediaFor(session: Pick<IStateClientSession, 'media'>, roomId: string | null): IEntityClientMedia {
  return session.media.roomId === roomId ? session.media : { ...NO_MEDIA, roomId };
}

function kept(input: IPropsClientInput, media: IEntityClientMedia): TSession<IEntityTableAck> {
  const next = draftOf(input);
  next.session.media = media;
  return decided(settleSession(input, next), APPLIED);
}

/** Turned off by something other than the player: the banner offers the reconnect. */
function turnedOff(media: IEntityClientMedia): IEntityClientMedia {
  return { ...media, enabled: false, autoDisconnected: true, idleBannerDismissed: false };
}

/** Turned on: the idle clock starts now, and an earlier automatic disconnect is over. */
function turnedOn(media: IEntityClientMedia, now: number): IEntityClientMedia {
  return { ...media, enabled: true, enabledAt: now, autoDisconnected: false };
}

/**
 * The 📡 control (frontend strict-ports plan, FE5c — useLiveKitSession's `toggleLkConnection`, moved). Only
 * at a table: the connection is per room.
 */
export function decideMediaToggled(input: IPropsClientInput, command: ICommandMediaClock): TSession<IEntityTableAck> {
  const roomId = input.table.room?.roomId ?? null;
  if (roomId === null) return decided(NOTHING, NOT_APPLIED);
  const media = mediaFor(input.session, roomId);
  const off: IEntityClientMedia = { ...media, enabled: false };
  return kept(input, media.enabled ? off : turnedOn(media, command.now));
}

/** The idle banner's or badge's Reconnect (`reconnectLk`). */
export function decideMediaReconnect(input: IPropsClientInput, command: ICommandMediaClock): TSession<IEntityTableAck> {
  const roomId = input.table.room?.roomId ?? null;
  if (roomId === null) return decided(NOTHING, NOT_APPLIED);
  return kept(input, turnedOn(mediaFor(input.session, roomId), command.now));
}

/**
 * The idle rule (§23.7): a connection left open with no gameplay for the timeout is turned off, to stop
 * accruing connection-time cost. Activity is the table's last move or the moment video was turned on,
 * whichever is later — the hook's clock, which every move and every (re)enable reset.
 */
export function decideMediaIdleChecked(input: IPropsClientInput, command: ICommandMediaIdle): TSession<IEntityTableAck> {
  const roomId = input.table.room?.roomId ?? null;
  const media = mediaFor(input.session, roomId);
  if (roomId === null || !media.enabled) return decided(NOTHING, NOT_APPLIED);
  const lastActivity = Math.max(input.table.lastGameActivityAt, media.enabledAt ?? 0);
  if (command.now - lastActivity < command.idleTimeoutMs) return decided(NOTHING, NOT_APPLIED);
  return kept(input, turnedOff(media));
}

/**
 * The server's idle sweep removed this player ON PURPOSE (§21.10: the whole table was idle). Rejoining by
 * itself would undo that, so it is the local idle rule's outcome: off, banner up, one tap reconnects.
 */
export function decideMediaRemovedByServer(input: IPropsClientInput, _command: ICommandNothing): TSession<IEntityTableAck> {
  const roomId = input.table.room?.roomId ?? null;
  if (roomId === null) return decided(NOTHING, NOT_APPLIED);
  return kept(input, turnedOff(mediaFor(input.session, roomId)));
}

/** The idle banner's ✕ (dismissed: the badge stays) — or a fresh disconnect surfacing it again. */
export function decideMediaBannerDismissed(input: IPropsClientInput, command: ICommandMediaBanner): TSession<IEntityTableAck> {
  const roomId = input.table.room?.roomId ?? null;
  if (roomId === null) return decided(NOTHING, NOT_APPLIED);
  const media: IEntityClientMedia = { ...mediaFor(input.session, roomId), idleBannerDismissed: command.dismissed };
  return kept(input, media);
}

/**
 * Report the player's first use of the microphone or the camera in a room — once per kind per room,
 * marked before the call as the hook's refs were. Guests are never reported (data minimisation: the
 * server discards their calls anyway, so the round-trip is skipped).
 */
export function decidePlanPresence(input: IPropsClientInput, command: ICommandMediaUsed): TSession<IEntityPresencePlan> {
  if (input.session.account.kind === 'guest') return decided(NOTHING, NO_PRESENCE);
  const media = mediaFor(input.session, command.roomId);
  const video = command.kind === 'video';
  if (video ? media.reportedVideo : media.reportedAudio) return decided(NOTHING, NO_PRESENCE);
  const next = draftOf(input);
  next.session.media = video ? { ...media, reportedVideo: true } : { ...media, reportedAudio: true };
  const request: IPayloadMediaPresence = { roomId: command.roomId, usedVideo: video, usedAudio: !video };
  const plan: IEntityPresencePlan = { request };
  return decided(settleSession(input, next), plan);
}
